'use client';
import { createContext, useState, useEffect, useMemo } from 'react';

export const ImpersonationContext = createContext({
  isImpersonating: false,
  setIsImpersonating: (_val) => {},
});

// Write localStorage + fire a same-tab custom event atomically.
// Call this instead of writing localStorage directly so context stays in sync.
export function broadcastImpersonationState(active) {
  if (active) {
    localStorage.setItem('impersonation', 'true');
  } else {
    localStorage.removeItem('impersonation');
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('impersonation-change', { detail: { active } }));
  }
}

export function ImpersonationProvider({ children }) {
  // Lazy initializer reads localStorage once on first render (SSR-safe)
  const [isImpersonating, setIsImpersonating] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('impersonation') === 'true';
  });

  useEffect(() => {
    // Same-tab: fired by broadcastImpersonationState() on start/stop
    const handleCustom = (e) => setIsImpersonating(Boolean(e.detail?.active));

    // Cross-tab: fired by the browser when another tab writes localStorage
    const handleStorage = (e) => {
      if (e.key !== 'impersonation' && e.key !== null) return;
      setIsImpersonating(localStorage.getItem('impersonation') === 'true');
    };

    window.addEventListener('impersonation-change', handleCustom);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('impersonation-change', handleCustom);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const value = useMemo(() => ({ isImpersonating, setIsImpersonating }), [isImpersonating]);

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
}
